import { SwitchButton, SwitchButtonState } from "components/SwitchButton/SwitchButton";
import { base64ToByteArray, handleTextInputNumberChange, hexToBase64, isMobileDevice, toHexString } from "helpers";
import { useState } from "react";
import { InitContextProps, useAuthContext } from "services/context/authentication.context";
import * as webEid from '@web-eid/web-eid-library/web-eid';
import React from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import SigningService from "services/signing.service";
import { POLL_MAX_RETRY_COUNT_ON_FAILED_REQUEST } from "services/authentication.service";
import { SigningAction } from "models/signing";
import { SigningPayload } from "services/signing.service";
import { getSigningErrorMessage, GENERIC_SIGNING_ERROR } from "helpers/signer-helper";

interface SigningFormProps {
    action: SigningAction;
    payload: SigningPayload;
    submitLabel: string;
    savingLabel?: string;
    onSuccess: (signingSessionId: string) => void;
    onCancel: () => void;
}

export function SigningForm(props: SigningFormProps) {
    const isMobile = isMobileDevice();
    const { state }: InitContextProps = useAuthContext();
    const { customerId, customer, user } = state;

    const [saving, setSaving] = useState(false);
    const [savingError, setSavingError] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [method, setMethod] = useState<string>('smart-id');
    const [activeSwitch, setActiveSwitch] = useState<SwitchButtonState>(SwitchButtonState.LEFT);
    const [personalCode, setPersonalCode] = useState<string>((user && user.personalIdentificationCode) || '');
    const [phoneNumber, setPhoneNumber] = useState<string>((customer && customer.phone) || '');
    const [verificationCode, setVerificationCode] = useState<string>('');
    const [intervalId, setIntervalId] = useState<number | undefined>();
    const [retryCount, setRetryCount] = useState(0);

    const onSigningSuccess = (signingSessionId: string) => {
        setSaving(false);
        props.onSuccess(signingSessionId);
    };

    const confirmSubmit = () => {
        if (method === 'smart-id') {
            beginSmartIdSigning();
        } else if (method === 'id-card') {
            beginIdCardSigning();
        } else {
            beginMobileIdSigning();
        }
    };

    const beginSmartIdSigning = () => {
        if (!customerId || !personalCode) return;

        setSaving(true);
        setRetryCount(0);
        SigningService.initSmartIdSigning(customerId, personalCode, props.payload, props.action).then(res => {
            setVerificationCode(res.verificationCode);
            const interval: any = setInterval(() => {
                const signingSessionId = res.signingSessionId;
                SigningService.pollSmartIdSigning(customerId, signingSessionId).then(pollResult => {
                    if (pollResult.status === 'COMPLETED') {
                        clearInterval(interval);
                        setVerificationCode('');
                        onSigningSuccess(signingSessionId);
                    } else if (pollResult.status === 'FAILED') {
                        clearInterval(interval);
                        setSaving(false);
                        setSavingError(true);
                        setErrorMessage(GENERIC_SIGNING_ERROR);
                    }
                }).catch(() => {
                    if (retryCount === POLL_MAX_RETRY_COUNT_ON_FAILED_REQUEST) {
                        clearInterval(interval);
                        setSaving(false);
                        setSavingError(true);
                        setErrorMessage(GENERIC_SIGNING_ERROR);
                    } else {
                        setRetryCount(retryCount + 1);
                    }
                });
            }, 1000);
            setIntervalId(interval);
        }).catch((e) => {
            setSaving(false);
            setSavingError(true);
            setErrorMessage(getSigningErrorMessage(e));
        });
    };

    const beginIdCardSigning = () => {
        if (!customerId) return;

        webEid.status()
            .then(() => {
                webEid.getSigningCertificate({ lang: 'et' })
                    .then(({ certificate }) => {
                        setSaving(true);
                        SigningService.initIdCardSigning(customerId, toHexString(base64ToByteArray(certificate)), props.payload, props.action).then((initResult) => {
                            webEid.sign(certificate, hexToBase64(initResult.hex), 'SHA-256', { lang: 'et' }).then(
                                ({ signature }) => {
                                    const signingSessionId = initResult.signingSessionId;
                                    SigningService.finalizeIdCardSigning(customerId, signingSessionId, toHexString(base64ToByteArray(signature)))
                                        .then(() => {
                                            onSigningSuccess(signingSessionId);
                                        }).catch((e) => {
                                            setSaving(false);
                                            setSavingError(true);
                                            setErrorMessage(getSigningErrorMessage(e));
                                        });
                                }
                            );
                        }).catch((e) => {
                            setSaving(false);
                            setSavingError(true);
                            setErrorMessage(getSigningErrorMessage(e));
                        });
                    }).catch((e) => {
                        setSaving(false);
                        setSavingError(true);
                        console.error(e);
                        setErrorMessage(GENERIC_SIGNING_ERROR);
                    });
            }).catch((e) => {
                setSaving(false);
                setSavingError(true);
                console.error(e);
                setErrorMessage(GENERIC_SIGNING_ERROR);
            });
    };

    const beginMobileIdSigning = () => {
        if (!customerId || !phoneNumber || !personalCode) return;

        setSaving(true);
        setRetryCount(0);
        SigningService.initMobileIdSigning(customerId, personalCode, phoneNumber, props.payload, props.action).then(res => {
            setVerificationCode(res);
            const interval: any = setInterval(() => {
                if (!customerId) return clearInterval(interval);

                const signingSessionId = res.signingSessionId;
                SigningService.pollMobileIdSigning(customerId, signingSessionId).then(pollResult => {
                    if (pollResult.status === 'COMPLETED') {
                        clearInterval(interval);
                        setVerificationCode('');
                        onSigningSuccess(signingSessionId);
                    } else if (pollResult.status === 'FAILED') {
                        clearInterval(interval);
                        setSaving(false);
                        setSavingError(true);
                        setErrorMessage(GENERIC_SIGNING_ERROR);
                    }
                }).catch(() => {
                    if (retryCount === POLL_MAX_RETRY_COUNT_ON_FAILED_REQUEST) {
                        clearInterval(interval);
                        setSaving(false);
                        setSavingError(true);
                        setErrorMessage(GENERIC_SIGNING_ERROR);
                    } else {
                        setRetryCount(retryCount + 1);
                    }
                });
            }, 1000);
            setIntervalId(interval);
        }).catch((e) => {
            setSaving(false);
            setSavingError(true);
            setErrorMessage(getSigningErrorMessage(e));
        });
    };

    const onMethodChange = (active: SwitchButtonState) => {
        let methodState = '';
        if (active === SwitchButtonState.LEFT) {
            methodState = 'smart-id';
        } else if (active === SwitchButtonState.MIDDLE) {
            methodState = 'id-card';
        } else {
            methodState = 'mobile-id';
        }
        setActiveSwitch(active);
        setMethod(methodState);
    };

    const handleCancel = () => {
        if (intervalId) clearInterval(intervalId);
        props.onCancel();
    };

    // Signing is only allowed as the logged in user, so the code is prefilled and locked when it is known.
    const isPersonalCodeLocked = !!(user && user.personalIdentificationCode);

    const personalCodeInput = (
        <input
            type="text"
            className="login__smart-id-input mb-1 mw-phone-input"
            placeholder='Isikukood'
            value={personalCode}
            onChange={(e) => setPersonalCode(handleTextInputNumberChange(personalCode, e.target.value))}
            disabled={saving || isPersonalCodeLocked}
        />
    );

    const getSigningType = () => {
        if (method === 'smart-id') {
            return (
                <div className="d-flex flex-column align-items-center login__container">
                    {personalCodeInput}
                </div>
            );
        } else if (method === 'id-card') {
            return (
                <div>Sisesta ID-kaart lugejasse ja vajuta nupule "{props.submitLabel}"</div>
            );
        } else {
            return (
                <div className="d-flex flex-column align-items-center login__container">
                    {personalCodeInput}
                    <div className="d-flex flex-row justify-content-center">
                        <input
                            type="text"
                            className="mobile-id-input__prefix mr-1"
                            placeholder='+372'
                            disabled
                        />
                        <input
                            type="tel"
                            className="mobile-id-input__main"
                            placeholder='Telefoninumber'
                            disabled={saving}
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(handleTextInputNumberChange(phoneNumber, e.target.value))}
                        />
                    </div>
                </div>
            );
        }
    };

    return (
        <>
            <Form noValidate onSubmit={(e: any) => {
                e.preventDefault();
                confirmSubmit();
            }} id={"signing-form"}>
                <div className="mt-4 row">
                    <SwitchButton
                        className={"default-switch mx-auto "}
                        onActiveChange={onMethodChange}
                        labelLeft={"Smart-ID"}
                        labelMiddle={"ID-kaart"}
                        labelRight={"Mobiil-ID"}
                        enableMiddleButton={!isMobile}
                        active={activeSwitch}
                    />
                </div>
                <div className="mt-5 row">
                    <div className="col-12 col-lg-8 offset-lg-2 text-center">
                        {
                            saving && verificationCode.length > 0 &&
                            <>
                                <p>Teie kontrollkood on</p>
                                <strong className="h1">{verificationCode}</strong>
                            </>
                        }
                        {getSigningType()}
                    </div>
                </div>
            </Form>
            <div className="d-flex flex-row align-items-end justify-content-around flex-wrap mb-1 mt-3">
                <Button variant="outline-dark"
                    onClick={handleCancel}
                    type="button"
                    className={"d-flex align-items-center"}>
                    <span className='px-2'>Tagasi</span>
                </Button>
                <Button variant="primary" onClick={confirmSubmit} type="submit" disabled={saving} form="signing-form"
                    className="d-flex align-items-center">
                    {
                        !saving && <span className='px-2'>{props.submitLabel}</span>
                    }
                    {
                        saving &&
                        <>
                            <span className='px-2'>{props.savingLabel || 'Allkirjastamine…'}</span>
                            <Spinner size={"sm"} animation={"border"} />
                        </>
                    }
                </Button>
            </div>
            {
                !saving && savingError &&
                <span className="d-flex justify-content-end text-danger px-5 pb-2">
                    {errorMessage || 'Viga allkirjastamisel, proovi mõne hetke pärast uuesti.'}
                </span>
            }
        </>
    );
}
